import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { dateAdd, isDateAfter, isDateBefore } from "app/util/date/tools/common"
import { messageSVC } from "message/service"
import { HOME_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import { userEventModule } from "user/event"
import {
  IUserImpersonator,
  IUserImpersonatorFormDataErrors,
  IUserImpersonatorHydrated,
  IUserInfo,
  IUserNewImpersonatorParams,
  USER_EVENTS
} from "user/model"
import { userRPO } from "user/repository"
import { getUserById, getUserId, getUserInfo, setUserInfo } from "./common"

export const isImpersonating = (userInfo?: IUserInfo) => (userInfo || getStore().getState().user).impersonate !== undefined

export const impersonate = (email: string): Promise<boolean> =>
  new Promise<boolean>(resolve => {
    messageSVC.confirmMessage({
      title: translate("user.impersonator.take.button"),
      message: translate("user.impersonator.take.confirm"),
      addGenericConfirmMessage: true,
      isWarning: true,
      onSuccess: async () => {
        const impersonateSession = await userRPO.impersonateSession(email)
        const userInfo = { ...getUserInfo() }
        userInfo.user = {
          ...userInfo.user,
          email: impersonateSession.email,
          firstname: impersonateSession.firstname,
          lastname: impersonateSession.lastname,
          id: impersonateSession.user_id
        }
        userInfo.token = impersonateSession.session_id
        userInfo.permissions = impersonateSession.permissions
        userInfo.tokenExpirationDateTime = dateAdd(new Date(), impersonateSession.ttl, "seconds")
        userEventModule.notify(USER_EVENTS.LOGOUT, {})
        setUserInfo(userInfo, impersonateSession)
        userEventModule.notify(USER_EVENTS.LOGIN, { userInfo })
        navigate(HOME_PATH)
        resolve(true)
      },
      onCancel: () => resolve(false)
    })
  })

export const getAvailableImpersonators = async (): Promise<IUserImpersonatorHydrated[]> =>
  hydrateImpersonators(
    (await userRPO.getAvailableImpersonators(getUserId())).filter(i => {
      if (!i.start_date && !i.end_date) {
        return true
      }
      const now = new Date()
      if (i.start_date) {
        if (isDateBefore(now, i.start_date)) {
          return false
        }
      }
      if (i.end_date) {
        if (isDateAfter(now, i.end_date)) {
          return false
        }
      }
      return true
    })
  )

export const getGivenImpersonators = async (): Promise<IUserImpersonatorHydrated[]> =>
  hydrateImpersonators(await userRPO.getImpersonators(getUserId()))

export const validateImpersonatorFormData = (data: IUserNewImpersonatorParams): IUserImpersonatorFormDataErrors => {
  const errors: IUserImpersonatorFormDataErrors = {}
  if (typeof data.impersonator_id !== "number" || data.impersonator_id < 0) {
    errors.impersonator_id = translate("label.field.required")
  }
  if (typeof data.start_date && typeof data.end_date) {
    const startDate = new Date(data.start_date as string)
    const endDate = new Date(data.end_date as string)
    if (endDate <= startDate) {
      errors.end_date = translate("label.field.date.start.greater.than.end")
    }
  }
  return errors
}

export const addImpersonator = async (params: IUserNewImpersonatorParams): Promise<IUserImpersonator | null> => {
  try {
    return await userRPO.addImpersonator(params)
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("user.impersonate.error.add"))
    return null
  }
}

export const updateImpersonator = async (params: IUserImpersonator): Promise<IUserImpersonator | null> => {
  try {
    return await userRPO.updateImpersonator(params)
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("user.impersonate.error.update"))
    return null
  }
}

export const deleteImpersonator = async (impersonatorId: number): Promise<boolean> =>
  new Promise<boolean>(resolve => {
    messageSVC.confirmMessage({
      title: translate("user.impersonate.delete.title"),
      message: translate("user.impersonate.delete.message"),
      onSuccess: async () => {
        try {
          await userRPO.deleteImpersonator(getUserId(), impersonatorId)
          resolve(true)
        } catch (error) {
          console.error(error)
          messageSVC.error(translate("user.impersonate.error.delete"))
          resolve(false)
        }
      },
      onCancel: () => resolve(false)
    })
  })

export const hydrateImpersonator = async (
  impersonator: IUserImpersonator | null | undefined
): Promise<IUserImpersonatorHydrated | null | undefined> => {
  if (!impersonator) {
    return impersonator
  }
  const user = await getUserById(impersonator.impersonator_id)
  return {
    ...impersonator,
    email: user.email,
    firstname: user.firstname || "",
    lastname: user.lastname || ""
  }
}

const hydrateImpersonators = async (impersonators: IUserImpersonator[]): Promise<IUserImpersonatorHydrated[]> => {
  const selfUserId = getUserId()
  if (impersonators.length === 0) {
    return []
  }
  const hydratedImpersonators: IUserImpersonatorHydrated[] = []
  const promises = impersonators.map(async i => {
    const user = await getUserById(i.user_id === selfUserId ? i.impersonator_id : i.user_id)
    hydratedImpersonators.push({
      ...i,
      email: user.email,
      firstname: user.firstname || "",
      lastname: user.lastname || ""
    } as IUserImpersonatorHydrated)
  })
  await Promise.all(promises)
  return hydratedImpersonators
}
